import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Page component that triggers Telegram notification for delivery
 * Route: /notify-driver/:orderId
 * 
 * When accessed (typically from email link), this page:
 * 1. Calls the backend API to send Telegram notification
 * 2. Shows success/error status to the user
 */
function NotifyDriverPage() {
    const { orderId } = useParams<{ orderId: string }>();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const hasSentRef = useRef(false);

    useEffect(() => {
        if (!orderId) {
            setStatus('error');
            setErrorMessage('Order ID is missing');
            return;
        }

        // Prevent double-send in React StrictMode (development only)
        if (hasSentRef.current) {
            return;
        }
        hasSentRef.current = true;

        const sendNotification = async () => {
            try {
                const response = await fetch(`/api/telegram/notify/${orderId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    setStatus('success');
                } else {
                    const data = await response.json();
                    setStatus('error');
                    setErrorMessage(data.error || 'Failed to send notification');
                }
            } catch (error) {
                setStatus('error');
                setErrorMessage('Network error - please try again');
            }
        };

        sendNotification();
    }, [orderId]);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: status === 'success' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : status === 'error'
                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                : 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
            padding: '20px',
        }}>
            <div style={{
                background: 'white',
                padding: '40px',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                textAlign: 'center',
                maxWidth: '400px',
                width: '100%',
            }}>
                {status === 'loading' && (
                    <>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>⏳</div>
                        <h1 style={{ color: '#2d3748', marginBottom: '10px' }}>Sending...</h1>
                        <p style={{ color: '#718096', lineHeight: 1.6 }}>
                            Sending delivery information to Telegram...
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
                        <h1 style={{ color: '#2d3748', marginBottom: '10px' }}>Notification Sent!</h1>
                        <p style={{ color: '#718096', lineHeight: 1.6 }}>
                            Delivery information has been successfully sent to the driver via Telegram.
                        </p>
                        {orderId && (
                            <div style={{
                                background: '#f7fafc',
                                padding: '12px',
                                borderRadius: '6px',
                                marginTop: '20px',
                                color: '#4a5568',
                                fontFamily: 'monospace',
                            }}>
                                Order: #{orderId.substring(0, 8)}...
                            </div>
                        )}
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
                        <h1 style={{ color: '#2d3748', marginBottom: '10px' }}>Notification Failed</h1>
                        <p style={{ color: '#718096', lineHeight: 1.6 }}>
                            {errorMessage || 'Failed to send Telegram notification. Please try again or contact support.'}
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: '20px',
                                padding: '12px 24px',
                                background: '#f5576c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                            }}
                        >
                            Try Again
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default NotifyDriverPage;
